import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from '@/core/services/open-api/api.service';
import {
    ActivitiesMetadataForm,
    Activity,
    ActivityFiltersForm,
    ActivityDto,
    ActivityToCreate,
    FilteredActivitiesResponse,
    FilteredActivitiesResponseDto,
    ActivityToCreateDto,
} from '@/core/models/activity';
import { HttpClient } from '@angular/common/http';
import { MomentHelper } from '@/core/models/moment-helper';
import { TimezonedDate } from '@/core/models/timezoned-date';

/**
 * @class ActivityRepository
 *
 * This class encapsulates the entire logic for API interaction for Activity models.
 *
 * The interface should only accept models and deliver models.
 * Internally models are transformed to DTOs or vice versa to achieve correct API communication.
 * The rest of the code should not be aware of the existence of DTOs at all.
 *
 * TODO: Evaluate changing the whole application setup to feature first architecture
 *       (move repositories to their features and not centrally in the "repositories" directory)
 *
 */
@Injectable({ providedIn: 'root' })
export class ActivityRepository extends ApiService {
    private readonly ACTIVITY_MAX = 50000;

    constructor(http: HttpClient) {
        super(http);
    }

    /**
     * Gets the count of activities of the current user
     * For regular users, this is the count of activities of themselves
     * For admins and managers, this is the count of activities of all users of the account.
     * @returns The activity count
     */
    public getActivityCount(): Observable<number> {
        return this._http.get<number>(ApiService.openApiUrl + 'activities/count');
    }

    public fetchActivitiesMeta(filterForAPI?: ActivityFiltersForm): Observable<ActivitiesMetadataForm> {
        if (filterForAPI?.startDate && filterForAPI?.endDate) {
            filterForAPI.startDate = MomentHelper.dateWithoutTimezone(filterForAPI.startDate);
            filterForAPI.endDate = MomentHelper.dateWithoutTimezone(filterForAPI.endDate);
        }
        return this._http.post<ActivitiesMetadataForm>(
            ApiService.openApiUrl + `activities/filters/meta?count=${this.ACTIVITY_MAX}&offset=0`,
            filterForAPI,
        );
    }

    public getFilteredActivities(
        offset: number,
        count: number,
        filterForAPI?: ActivityFiltersForm,
        sortField?: string,
        sortOrder?: number,
    ): Observable<FilteredActivitiesResponse> {
        if (filterForAPI?.startDate && filterForAPI?.endDate) {
            filterForAPI.startDate = MomentHelper.dateWithoutTimezone(filterForAPI.startDate);
            filterForAPI.endDate = MomentHelper.dateWithoutTimezone(filterForAPI.endDate);
        }

        let url =
            ApiService.openApiUrl + 'activities/filters?embed=tile&version=v1&count=' + count + '&offset=' + offset;
        if (sortField) {
            url += `&sortField=${sortField}`;
        }
        if (sortOrder !== undefined) {
            url += `&sortOrder=${sortOrder}`;
        }
        return this._http
            .post<FilteredActivitiesResponseDto>(url, filterForAPI)
            .pipe(
                map((reponseDto: FilteredActivitiesResponseDto) =>
                    this.mapFilteredActivitiesResponseDtoToActivity(reponseDto),
                ),
            );
    }

    public getById(id: number): Observable<Activity> {
        return this._http
            .get<ActivityDto>(ApiService.openApiUrl + 'activities/' + id)
            .pipe(map((responseDto: ActivityDto) => this.mapDtoToActivity(responseDto)));
    }

    public deleteById(id: number): Observable<void> {
        return this._http.delete<void>(ApiService.openApiUrl + 'activities/' + id);
    }

    public deleteByIds(ids: number[]): Observable<void> {
        return this._http.delete<void>(ApiService.openApiUrl + 'activities/bulk', {
            body: { ids },
        });
    }

    public billedEditBulk(ids: number[], billed: boolean): Observable<void> {
        return this._http.post<void>(ApiService.openApiUrl + 'activities/bulk', {
            ids,
            billed,
        });
    }

    public create(activity: ActivityToCreate): Observable<Activity> {
        const dto = this.mapNewActivityToDto(activity);
        return this._http
            .post<ActivityDto>(ApiService.openApiUrl + 'activities', dto)
            .pipe(map((responseDto: ActivityDto) => this.mapDtoToActivity(responseDto)));
    }

    public update(activity: Activity): Observable<void> {
        const dto = this.mapActivityToDto(activity);
        return this._http.put<void>(ApiService.openApiUrl + 'activities/' + dto.id, dto);
    }

    public delete(activity: Activity): Observable<void> {
        return this.deleteById(activity.id);
    }

    private mapNewActivityToDto(activity: ActivityToCreate): ActivityToCreateDto {
        const { tileIds, startDate, endDate, ...fields } = activity;
        return {
            startDate: startDate.toApiJson(),
            startUtcOffset: startDate.getApiUtcOffset(),
            endDate: endDate.toApiJson(),
            endUtcOffset: endDate.getApiUtcOffset(),
            tiles: tileIds,
            ...fields,
        };
    }

    private mapActivityToDto(activity: Activity): ActivityDto {
        const { tileIds, startDate, endDate, ...fields } = activity;
        return {
            startDate: startDate.toApiJson(),
            startUtcOffset: startDate.getApiUtcOffset(),
            endDate: endDate.toApiJson(),
            endUtcOffset: endDate.getApiUtcOffset(),
            tiles: tileIds,
            ...fields,
        };
    }

    // this is a helper for fixing bad offsets in the database, e.g. +5:00, which should be +05:00
    private fixOffset(offset: string): string {
        if (offset === '-00:00' || offset === '-0:00') {
            console.debug('fixed offset on loading activity: ' + offset);
            return '+00:00';
        }
        if (offset.length === 5) {
            console.debug('fixed offset on loading activity: ' + offset);
            return offset.charAt(0) + '0' + offset.substring(1);
        }
        if (offset.length < 6) {
            console.error('ERROR - ActivityRepository: Encountered bad offset: ' + offset);
        }
        return offset;
    }

    private mapDtoToActivity(dto: ActivityDto): Activity {
        const { tiles, startDate, endDate, startUtcOffset, endUtcOffset, ...fields } = dto;
        return {
            startDate: TimezonedDate.fromApi(startDate, this.fixOffset(startUtcOffset)),
            endDate: TimezonedDate.fromApi(endDate, this.fixOffset(endUtcOffset)),
            tileIds: tiles,
            ...fields,
        };
    }

    private mapFilteredActivitiesResponseDtoToActivity(dto: FilteredActivitiesResponseDto): FilteredActivitiesResponse {
        const activities = [];
        for (const activityDto of dto.activities) {
            activities.push(this.mapDtoToActivity(activityDto));
        }
        return {
            totalCount: dto.totalCount,
            totalDuration: dto.totalDuration,
            activities,
        };
    }
}
